export class AudioManager {
  private context: AudioContext | null = null;
  private listener: AudioListener | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private voiceGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private sources = new Map<string, AudioBufferSourceNode>();
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;
    this.context = new AudioContext();
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }
    this.masterGain = this.context.createGain();
    this.masterGain.connect(this.context.destination);

    this.musicGain = this.context.createGain();
    this.musicGain.connect(this.masterGain);

    this.sfxGain = this.context.createGain();
    this.sfxGain.connect(this.masterGain);

    this.voiceGain = this.context.createGain();
    this.voiceGain.connect(this.masterGain);

    this.ambientGain = this.context.createGain();
    this.ambientGain.connect(this.masterGain);

    this.initialized = true;
  }

  setMasterVolume(vol: number): void {
    if (this.masterGain) this.masterGain.gain.value = Math.max(0, Math.min(1, vol));
  }

  setMusicVolume(vol: number): void {
    if (this.musicGain) this.musicGain.gain.value = Math.max(0, Math.min(1, vol));
  }

  setSfxVolume(vol: number): void {
    if (this.sfxGain) this.sfxGain.gain.value = Math.max(0, Math.min(1, vol));
  }

  setVoiceVolume(vol: number): void {
    if (this.voiceGain) this.voiceGain.gain.value = Math.max(0, Math.min(1, vol));
  }

  setAmbientVolume(vol: number): void {
    if (this.ambientGain) this.ambientGain.gain.value = Math.max(0, Math.min(1, vol));
  }

  async loadSound(id: string, url: string): Promise<void> {
    if (!this.context) return;
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      this.buffers.set(id, audioBuffer);
    } catch {
      console.warn(`Failed to load sound: ${id}`);
    }
  }

  playSfx(id: string, volume = 1, pitch = 1, x = 0, y = 0, z = 0): string {
    if (!this.context || !this.sfxGain) return '';
    const buffer = this.buffers.get(id);
    if (!buffer) return '';

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = pitch;

    const gain = this.context.createGain();
    gain.gain.value = volume;
    source.connect(gain);

    if (x !== 0 || y !== 0 || z !== 0) {
      const panner = this.context.createPanner();
      panner.panningModel = 'HRTF';
      panner.distanceModel = 'inverse';
      panner.refDistance = 10;
      panner.maxDistance = 100;
      panner.setPosition(x, y, z);
      gain.connect(panner);
      panner.connect(this.sfxGain);
    } else {
      gain.connect(this.sfxGain);
    }

    source.start();
    const handleId = `sfx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.sources.set(handleId, source);
    source.onended = () => {
      this.sources.delete(handleId);
    };
    return handleId;
  }

  stopSound(handleId: string): void {
    const source = this.sources.get(handleId);
    if (source) {
      source.stop();
      this.sources.delete(handleId);
    }
  }

  updateListenerPosition(x: number, y: number, z: number, yaw: number, pitch: number): void {
    if (!this.context) return;
    const listener = this.context.listener;
    if (listener.positionX) {
      listener.positionX.value = x;
      listener.positionY.value = y;
      listener.positionZ.value = z;
    }
    const forwardX = -Math.sin(yaw) * Math.cos(pitch);
    const forwardY = Math.sin(pitch);
    const forwardZ = -Math.cos(yaw) * Math.cos(pitch);
    if (listener.forwardX) {
      listener.forwardX.value = forwardX;
      listener.forwardY.value = forwardY;
      listener.forwardZ.value = forwardZ;
    }
  }

  stopAll(): void {
    for (const [id, source] of this.sources) {
      try { source.stop(); } catch { /* already stopped */ }
    }
    this.sources.clear();
  }

  dispose(): void {
    this.stopAll();
    this.context?.close();
    this.initialized = false;
  }
}
